import './App.css';
import React from 'react';
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./Login";
import SsoCallback from "./SsoCallback";
import Register from "./Register";
import ResetPassword from "./ResetPassword";
import ForcePasswordChange from "./ForcePasswordChange";
import Dashboard from './components/Dashboard/Dashboard';
import AdminHome from './components/Admin/AdminHome';
import PeekBanner from './components/PeekBanner';
import { isPeeking } from './utils/peek';
import AcceptInvite from './components/AcceptInvite';
import TrustCenterPage from './components/TrustCenter/TrustCenterPage';
import AuditorPortalPage from './components/Compliance/AuditorPortalPage';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// All org roles that belong in the /dashboard — includes org admins, members, and partners
const DASHBOARD_ORG_ROLES = ['ORG_ADMIN', 'MEMBER', 'CISO_RESELLER', 'CPA_AUDITOR', 'CPA_ADMIN'];

// Both platform roles enter the admin console. platform_user is restricted WITHIN the console
// (no billing, no org archive) — those are gated per-feature, not at the route boundary.
const PLATFORM_STAFF_ROLES = ['ROLE_PLATFORM_ADMIN', 'ROLE_PLATFORM_USER'];

function ProtectedRoute({ element, role, redirectPath = "/login" }) {
  const userRole = localStorage.getItem("role");
  const orgRole  = localStorage.getItem("orgRole") || '';

  if (!userRole) {
    return <Navigate to={redirectPath} />;
  }

  if (role === 'ROLE_PLATFORM_ADMIN') {
    // The /adminHome route admits BOTH platform roles; platform_user is restricted per-feature inside.
    if (!PLATFORM_STAFF_ROLES.includes(userRole)) {
      return <Navigate to={redirectPath} />;
    }
  } else if (role === 'ROLE_USER') {
    // Platform staff browse the customer dashboard during an active peek session
    if (PLATFORM_STAFF_ROLES.includes(userRole) && isPeeking()) {
      return element;
    }
    // Any org role (ORG_ADMIN, MEMBER, partner roles) belongs in the dashboard
    if (userRole !== 'ROLE_USER' && !DASHBOARD_ORG_ROLES.includes(orgRole)) {
      return <Navigate to={redirectPath} />;
    }
  } else if (role && userRole !== role) {
    return <Navigate to={redirectPath} />;
  }

  return element;
}

// Root path. Sending authenticated users to /login breaks the browser Back
// button (after a refresh/direct-open the prior history entry is "/", so Back
// would bounce a logged-in user to login). Route them into the app instead;
// only truly unauthenticated users go to /login.
function RootRedirect() {
  const userRole = localStorage.getItem("role");
  const orgRole  = localStorage.getItem("orgRole") || '';
  if (!userRole) return <Navigate to="/login" replace />;
  if (PLATFORM_STAFF_ROLES.includes(userRole)) return <Navigate to="/adminHome" replace />;
  if (userRole === 'ROLE_USER' || DASHBOARD_ORG_ROLES.includes(orgRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <HashRouter>
        <PeekBanner />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/sso/callback" element={<SsoCallback />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password-required" element={<ForcePasswordChange />} />
          <Route 
            path="/dashboard/*" 
            element={
              <ProtectedRoute 
                element={<Dashboard />} 
                role="ROLE_USER"
              />
            }
          />

          <Route
            path="/adminHome/*"
            element={
              <ProtectedRoute
                element={<AdminHome />}
                role="ROLE_PLATFORM_ADMIN"
              />
            }
          />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/trust/:slug"   element={<TrustCenterPage />} />
          <Route path="/auditor/:token" element={<AuditorPortalPage />} />
        </Routes>
      </HashRouter>  
    </div>
  );
}

export default App;
